import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { type FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { CardRowSkeleton, DialogComponent, formatDateTimeFromISO, Modal, VStack } from "widgets";
import { useOverlay, useVisuallyLoading } from "widgets/hooks";
import { getUnknownErrorMessage } from "widgets/utils";

import { fileNameValidation, simpleFieldValidation, urlValidation } from "@/app-utils/form.ts";
import { query as Q } from "@/app-utils/graphql/query.ts";
import { useError } from "@/app-utils/graphql/useError.ts";
import type { InputModifyProjectFileInput, Organism, ProjectFile } from "@/graphql.ts";
import { HeaderIcon } from "@/modules/files/components/HeaderIcon";
import {
  type GetFileEditingQuery,
  useGetFileEditingLazyQuery,
  ValidateFileNamesDocument,
  type ValidateFileNamesQuery,
  type ValidateFileNamesQueryVariables,
} from "@/modules/files/graphql/files-queries.gql";
import { getFileInfo, isAnalysisSetFile, isGenomeViewerFile } from "@/modules/files/utils/file";
import { updateProjectFile } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { Addon } from "@/ui-modules/core/redux-form/Addon.tsx";
import InputField, { InputReduxFormField } from "@/ui-modules/core/redux-form/InputField.tsx";
import { SequencedGenomeSelector } from "@/ui-modules/files/SequencedGenomeSelector.tsx";

type EditableFile = NonNullable<GetFileEditingQuery["projectFileById"]>;

interface FileEditDialogFormData {
  [FileInfoEditDialogFieldNames.NAME_FIELD_NAME]: string;
  [FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME]: string | null;
  [FileInfoEditDialogFieldNames.EDITED_FILE]: EditableFile;
  [FileInfoEditDialogFieldNames.GENOME_FIELD_NAME]: Organism[];
  [FileInfoEditDialogFieldNames.URL_FIELD_NAME]: string | null;
}

interface FileEditDialogProps {
  file: Pick<ProjectFile, "id" | "fileName" | "fileBrowseType" | "lastModified">;
  onSaved?: () => void;
  isOpen: boolean;
  onCancel: () => void;
}

enum FileInfoEditDialogFormNames {
  FORM_NAME = "fileEditingForm",
}

enum FileInfoEditDialogFieldNames {
  NAME_FIELD_NAME = "NAME_FIELD_NAME",
  DESCRIPTION_FIELD_NAME = "DESCRIPTION_FIELD_NAME",
  EDITED_FILE = "EDITED_FILE",
  GENOME_FIELD_NAME = "GENOME_FIELD_NAME",
  URL_FIELD_NAME = "URL_FIELD_NAME",
}

const query = Q<ValidateFileNamesQuery, ValidateFileNamesQueryVariables>(ValidateFileNamesDocument);

const uniqueFileNameValidation = async (values: Partial<FileEditDialogFormData>) => {
  const fileName = values[FileInfoEditDialogFieldNames.NAME_FIELD_NAME];
  const editedFile = values[FileInfoEditDialogFieldNames.EDITED_FILE];
  const { extension } = getFileInfo(editedFile?.fileName ?? "", editedFile?.fileBrowseType ?? "");
  const nextFileName = fileName?.concat(extension);

  if (nextFileName !== editedFile?.fileName) {
    const folderId = editedFile?.parentFolderIds?.slice(-1)?.[0];

    if (folderId != undefined && nextFileName != undefined) {
      const result = await query({
        folderId,
        fileNames: [nextFileName],
      });

      if ((result.data?.fileNameExistsInCollector?.length ?? 0) !== 0) {
        throw {
          [FileInfoEditDialogFieldNames.NAME_FIELD_NAME]: "This name already exists.",
        };
      }
    }
  }
};

interface FileEditDialogFormContentProps {
  file: Pick<ProjectFile, "id" | "fileName" | "fileBrowseType" | "lastModified">;
  isOpen: boolean;
  onCancel: () => void;
  onSaved?: () => void;
}

const FileEditDialogFormContent: FC<
  FileEditDialogFormContentProps & InjectedFormProps<FileEditDialogFormData, FileEditDialogFormContentProps>
> = ({ file, isOpen, onCancel, onSaved, initialize, handleSubmit, error: formError }) => {
  const dispatch = useDispatch<ReduxDispatch>();

  // Fetch editable file information
  const [loadFileEditingData, { data, loading, error }] = useGetFileEditingLazyQuery({
    variables: { fileId: file.id },
  });
  void useError(error);

  useEffect(() => {
    if (isOpen) {
      void loadFileEditingData();
    }
  }, [isOpen, loadFileEditingData]);

  const fileEditingData = data?.projectFileById;

  const { baseName, extension } = getFileInfo(fileEditingData?.fileName ?? "", fileEditingData?.fileBrowseType ?? "");

  const handleSubmitData = async (values: FileEditDialogFormData) => {
    const newFileName = values[FileInfoEditDialogFieldNames.NAME_FIELD_NAME];
    const newURL = values[FileInfoEditDialogFieldNames.URL_FIELD_NAME];
    const description = values[FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME];
    const selectedOrganisms = values[FileInfoEditDialogFieldNames.GENOME_FIELD_NAME];
    const organismIds: number[] = selectedOrganisms ? selectedOrganisms.map((o: Organism) => o.id) : [];

    const modifyProjectFileInput: InputModifyProjectFileInput = {
      id: fileEditingData?.id,
      name: newFileName?.concat(extension),
      url: newURL,
      description,
      organismIds,
    };

    return await dispatch(updateProjectFile(modifyProjectFileInput))
      .then(onSaved)
      .catch((error: unknown) => {
        throw new SubmissionError({
          _error: getUnknownErrorMessage(error, "An error occurred while saving the file."),
        });
      });
  };

  const fakeLoading = useVisuallyLoading(loading, 300);

  useEffect(() => {
    if (isOpen && fileEditingData) {
      initialize({
        [FileInfoEditDialogFieldNames.NAME_FIELD_NAME]: baseName,
        [FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME]: fileEditingData.description,
        [FileInfoEditDialogFieldNames.EDITED_FILE]: fileEditingData,
        [FileInfoEditDialogFieldNames.URL_FIELD_NAME]: fileEditingData.url,
        [FileInfoEditDialogFieldNames.GENOME_FIELD_NAME]: (fileEditingData.organisms ?? []).filter(
          (o): o is Organism => o !== null,
        ),
      });
    }
  }, [isOpen, fileEditingData, baseName, initialize]);

  return (
    <DialogComponent
      title={file.fileName ?? undefined}
      subtitle={
        file.lastModified ? `Modified on ${formatDateTimeFromISO(file.lastModified, { hideTime: true })}` : undefined
      }
      icon={file ? <HeaderIcon file={file} /> : undefined}
      closeFn={onCancel}
      width={580}
      mainButton={{ label: "Save", onPress: handleSubmit(handleSubmitData) }}
      errorCallout={formError ? { message: formError } : undefined}
    >
      {loading || fakeLoading ? (
        <VStack spacing={15}>
          <CardRowSkeleton />
          <CardRowSkeleton />
        </VStack>
      ) : (
        fileEditingData && (
          <>
            <InputReduxFormField
              componentClass="input"
              name={FileInfoEditDialogFieldNames.NAME_FIELD_NAME}
              label={"Name"}
              placeholder={`Enter a name`}
              maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.NAME}
              component={InputField}
              key={FileInfoEditDialogFieldNames.NAME_FIELD_NAME}
              validate={[simpleFieldValidation, fileNameValidation]}
              suffix={<Addon label={extension} />}
            />
            {fileEditingData.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK && (
              <InputReduxFormField
                componentClass="input"
                name={FileInfoEditDialogFieldNames.URL_FIELD_NAME}
                label={"Link"}
                maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.URL}
                type="url"
                placeholder={`Enter link or URL`}
                component={InputField}
                validate={urlValidation}
              />
            )}
            {(fileEditingData.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE ||
              fileEditingData.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY ||
              fileEditingData.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK) && (
              <InputReduxFormField
                componentClass="input"
                name={FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME}
                label={"Description"}
                placeholder={`Enter a description`}
                maxLength={DB_FIELD_MAX_LENGTH.RESOURCE.DESCRIPTION}
                component={InputField}
                key={FileInfoEditDialogFieldNames.DESCRIPTION_FIELD_NAME}
              />
            )}
            {fileEditingData.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE &&
              (isAnalysisSetFile(fileEditingData) ||
                isGenomeViewerFile(fileEditingData) ||
                (fileEditingData.organisms?.length ?? 0) > 0) && (
                <SequencedGenomeSelector
                  fieldName={FileInfoEditDialogFieldNames.GENOME_FIELD_NAME}
                  name={"Genome"}
                  submitting={false}
                  clearable={true}
                  multiple={true}
                />
              )}
          </>
        )
      )}
    </DialogComponent>
  );
};

const FileEditDialogFormContentForm = reduxForm<FileEditDialogFormData, FileEditDialogFormContentProps>({
  form: FileInfoEditDialogFormNames.FORM_NAME,
  asyncBlurFields: [FileInfoEditDialogFieldNames.NAME_FIELD_NAME],
  asyncValidate: uniqueFileNameValidation,
})(FileEditDialogFormContent);

export const FileEditDialogForm: FC<FileEditDialogProps> = ({ isOpen, onCancel, onSaved, file }) => {
  const { state, overlayProps } = useOverlay({
    isOpen: isOpen,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        onCancel();
      }
    },
  });

  return (
    <Modal state={state} {...overlayProps}>
      <FileEditDialogFormContentForm file={file} isOpen={state.isOpen} onSaved={onSaved} onCancel={state.close} />
    </Modal>
  );
};
